"use client"

import { useTranslations } from "next-intl"

import { Link } from "@i18n/navigation"
import { SITE_NAME } from "@lib/util/env"
import { ArrowUpMini } from "@medusajs/icons"
import { PboproMark } from "@/components/icons/pbopro-mark"
import { DiscordIcon } from "@/components/ui/discord"
import { YoutubeIcon } from "@/components/ui/youtube"

/**
 * Social links — YouTube and Discord point at the real channels (2026-10-21).
 * No GitHub entry: the account is suspended and the site links to GitHub
 * nowhere, footer included (2026-10-21).
 */
const socialLinks = [
  {
    href: "https://www.youtube.com/@My-666-abc/videos",
    label: "YouTube",
    Icon: YoutubeIcon,
  },
  {
    href: "https://discord.gg/BqgkQ3wxZr",
    label: "Discord",
    Icon: DiscordIcon,
  },
] as const

/**
 * Minimal marketing footer (adapted from the footers-minimal block). Client
 * component because of the back-to-top control. Every link is a real route or
 * on-page anchor — no "#" placeholders. The /changelog page ships with the
 * next task; the link is already live here by design.
 */
export default function Footer() {
  const t = useTranslations("Footer")

  const productLinks = [
    { href: "/", label: t("home") },
    { href: "/#preview", label: t("preview") },
    { href: "/#features", label: t("features") },
    { href: "/#pricing", label: t("pricing") },
    { href: "/#faq", label: t("faq") },
    { href: "/changelog", label: t("changelog") },
  ] as const

  const legalLinks = [
    { href: "/legal/terms", label: t("terms") },
    { href: "/legal/privacy", label: t("privacy") },
    { href: "/legal/refunds", label: t("refunds") },
    { href: "/legal/cookies", label: t("cookies") },
  ] as const

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <footer className="w-full border-t bg-ui-bg-subtle">
      <div className="container mx-auto px-4 py-12 md:px-6 2xl:max-w-[1400px]">
        <div className="flex flex-col justify-between md:flex-row">
          <div className="mb-8 md:mb-0">
            <Link
              href="/"
              className="flex items-center space-x-2 txt-compact-medium-plus font-semibold text-ui-fg-base hover:text-ui-fg-subtle transition-colors"
              data-testid="footer-home-link"
            >
              <PboproMark aria-hidden className="h-6 w-auto shrink-0" />
              {SITE_NAME}
            </Link>
            <p className="text-muted-foreground mt-4 max-w-xs text-sm">
              {t("tagline")}
            </p>
            <div
              className="mt-6 flex items-center gap-x-4"
              data-testid="footer-socials"
            >
              {socialLinks.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon size={22} />
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-2">
            <div className="space-y-3">
              <h3 className="text-sm font-medium">{t("productHeading")}</h3>
              <ul className="space-y-2">
                {productLinks.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={href}
                      className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium">{t("legalHeading")}</h3>
              <ul className="space-y-2">
                {legalLinks.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={href}
                      className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col-reverse items-center justify-between gap-4 border-t pt-8 md:flex-row">
          <div className="text-muted-foreground text-center text-sm md:text-left">
            <p>
              {t("copyright", {
                year: new Date().getFullYear(),
                siteName: SITE_NAME,
              })}
            </p>
            <p className="txt-compact-small">{t("operator")}</p>
          </div>
          <button
            type="button"
            onClick={scrollToTop}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm transition-colors"
            aria-label={t("backToTop")}
          >
            {t("backToTop")} <ArrowUpMini className="h-4 w-4" />
          </button>
        </div>
      </div>
    </footer>
  )
}
